import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShoppingBag, ArrowLeft, Lock, User, Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = ({ user, onLogout }) => {
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas nuevas no coinciden');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/change-password`,
        {
          old_password: passwordData.old_password,
          new_password: passwordData.new_password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('¡Contraseña cambiada exitosamente!');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      setShowPasswordChange(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al cambiar contraseña';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/catalog" className="flex items-center space-x-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Volver al catálogo</span>
            </Link>
            <Link to="/" className="flex items-center space-x-2">
              <ShoppingBag className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-slate-900">HI-TEC B2B</h1>
            </Link>
          </div>
        </div>
      </header>

      {/* Profile Content */}
      <main className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Mi Perfil</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* User Information */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <User className="w-6 h-6 mr-2 text-blue-600" />
              Información Personal
            </h2>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-600">Nombre del Negocio</p>
                  <p className="font-semibold text-slate-900">{user.business_name}</p>
                </div>
              </div>

              {user.contact_name && (
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-600">Responsable</p>
                    <p className="font-semibold text-slate-900">{user.contact_name}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-600">Email</p>
                  <p className="font-semibold text-slate-900">{user.email}</p>
                </div>
              </div>

              {user.phone && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-600">Teléfono</p>
                    <p className="font-semibold text-slate-900">{user.phone}</p>
                  </div>
                </div>
              )}

              {user.province && (
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-600">Provincia</p>
                    <p className="font-semibold text-slate-900">{user.province}</p>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-600">Estado de la Cuenta</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  user.approved 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {user.approved ? '✓ Cuenta Aprobada' : '⏳ Pendiente de Aprobación'}
                </span>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <Lock className="w-6 h-6 mr-2 text-blue-600" />
              Seguridad
            </h2>

            {!showPasswordChange ? (
              <div>
                <p className="text-slate-600 mb-4">
                  Mantén tu cuenta segura cambiando tu contraseña regularmente.
                </p>
                <Button
                  onClick={() => setShowPasswordChange(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Cambiar Contraseña
                </Button>
              </div>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="old_password">Contraseña Actual</Label>
                  <Input
                    id="old_password"
                    type="password"
                    value={passwordData.old_password}
                    onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <Label htmlFor="new_password">Nueva Contraseña</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                    minLength={6}
                    className="mt-1"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <Label htmlFor="confirm_password">Confirmar Nueva Contraseña</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                    minLength={6}
                    className="mt-1"
                    placeholder="••••••••"
                  />
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={loading}
                  >
                    {loading ? 'Cambiando...' : 'Confirmar Cambio'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowPasswordChange(false);
                      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
